"use client";

import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import DescriptionIcon from "@mui/icons-material/Description";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import SearchIcon from "@mui/icons-material/Search";
import {
	Autocomplete,
	Box,
	Button,
	Card,
	CardContent,
	Chip,
	CircularProgress,
	Divider,
	Grid,
	Paper,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import "dayjs/locale/it";
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { tenant } from "../../tenant";

import { AuthContext } from "context";
import { createMRTColumnHelper } from "material-react-table";
import DashboardLayout from "../../examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "../../examples/Navbars/DashboardNavbar";
import apiService from "../../services/api-service";
import GenericTable from "../genericTable";
// GraphQL import removed - now using REST API

const GarbageExport = () => {
	const navigate = useNavigate();
	const authContext = React.useContext(AuthContext);
	const [loading, setLoading] = useState(false);
	const [searchLoading, setSearchLoading] = useState(false);
	const [documents, setDocuments] = useState([]);
	const [documentType, setDocumentType] = useState("first-copy"); // 'first-copy' or 'fourth-copy'
	const [aree, setAree] = useState([]);
	const [selectedArea, setSelectedArea] = useState(null);
	const [startDate, setStartDate] = useState(null);
	const [endDate, setEndDate] = useState(null);
	const [areaFromStorage, setAreaFromStorage] = useState(false);
	const [hasSearched, setHasSearched] = useState(false);
	const [loadingDocuments, setLoadingDocuments] = useState(false);

	const columnHelper = createMRTColumnHelper();

	const handleSetNotification = (msg, severityType) => {
		if (severityType === "success") {
			toast.success(msg, { id: "garbage-export-toast" });
		} else {
			toast.error(msg, { id: "garbage-export-toast" });
		}
	};

	useEffect(() => {
		fetchAree();
	}, []);

	const fetchAree = async () => {
		setLoading(true);
		apiService
			.get("/area-management/", {}, {})
			.then((data) => {
				if (data) {
					setAree(data);

					// Controlla se l'ID dell'area in sessionStorage è presente nell'array delle aree
					const storedAreaId = localStorage.getItem("areaSelected");
					if (storedAreaId) {
						const foundArea = data.find((area) => area.id == storedAreaId);
						if (foundArea) {
							setSelectedArea(foundArea);
							setAreaFromStorage(true);
						}
					}
				}
			})
			.catch((error) => {
				console.log(error);
				handleSetNotification(
					"Qualcosa è andato storto durante il caricamento delle aree. Riprovare più tardi, se l'errore persiste contatti il supporto tecnico segnalando l'errore #009839",
					"error"
				);
			})
			.finally(() => {
				setLoading(false);
			});
	};

	const fetchDocuments = async () => {
		// Verifica che sia selezionata un'area
		if (!selectedArea) {
			handleSetNotification("Seleziona un'area prima di effettuare la ricerca", "warning");
			return;
		}

		setSearchLoading(true);
		setHasSearched(true);

		// Prepare query parameters
		const params = {};

		// Add area filter if selected
		if (selectedArea) {
			params.area_id = selectedArea.id;
		}

		// Add date filters if selected
		if (startDate) {
			params.start_date = dayjs(startDate).format("YYYY-MM-DD");
		}

		if (endDate) {
			params.end_date = dayjs(endDate).format("YYYY-MM-DD");
		}

		// Determine endpoint based on document type
		const endpoint = documentType === "first-copy" ? "/garbage-first-copy/" : "/garbage-fourth-copy/";
		apiService
			.get(endpoint, params, {})
			.then((response) => {
				if (response) setDocuments(response);
			})
			.catch((error) => {
				handleSetNotification("Errore durante la chiamata al server, codice d'errore #08514262429906", "error");
			})
			.finally(() => {
				setSearchLoading(false);
			});
	};

	const columns_first_copy = [
		columnHelper.accessor("date", {
			header: "Data",
			size: 100,
			enableSorting: true,
			enableColumnFilterModes: true,
		}),
		columnHelper.accessor("url", {
			header: "Documento",
			size: 100,
			enableSorting: false,
			enableColumnFilterModes: false,
			enableColumnFilter: false,
			Cell: ({ cell }) => (
				<Button
					variant="contained"
					color="primary"
					size="small"
					onClick={() => window.open(cell.getValue(), "_blank")}
					sx={{
						textTransform: "none",
						boxShadow: "none",
						borderRadius: "8px",
						fontSize: "0.8rem",
						color: "white!important",
					}}
				>
					Visualizza
				</Button>
			),
		}),
		columnHelper.accessor("supplier", {
			header: "Fornitore",
			size: 100,
			enableSorting: true,
			enableColumnFilterModes: true,
		}),
		columnHelper.accessor("cer_code", {
			header: "CER",
			size: 100,
			enableSorting: true,
			enableColumnFilterModes: true,
		}),
		columnHelper.accessor("wastes", {
			header: "Rifiuti",
			size: 100,
			enableSorting: true,
			enableColumnFilterModes: true,
			Cell: ({ cell }) => {
				const wastes = cell.getValue();

				return (
					<Box>
						{wastes.map((waste, index) => (
							<Typography key={index} variant="body2" sx={{ fontSize: "0.875rem", color: "#555" }}>
								{waste.material}: <Chip size="small" label={waste.quantity} sx={{ fontSize: "0.75rem" }} />
							</Typography>
						))}
					</Box>
				);
			},
		}),
	];

	const columns_fourth_copy = [
		columnHelper.accessor("date", {
			header: "Data",
			size: 100,
			enableSorting: true,
			enableColumnFilterModes: true,
		}),
		columnHelper.accessor("url", {
			header: "Documento",
			size: 100,
			enableSorting: false,
			enableColumnFilterModes: false,
			enableColumnFilter: false,
			Cell: ({ cell }) => (
				<Button
					variant="contained"
					color="primary"
					size="small"
					onClick={() => window.open(cell.getValue(), "_blank")}
					sx={{
						textTransform: "none",
						boxShadow: "none",
						borderRadius: "8px",
						fontSize: "0.8rem",
						color: "white!important",
					}}
				>
					Visualizza
				</Button>
			),
		}),
		columnHelper.accessor("supplier", {
			header: "Fornitore",
			size: 100,
			enableSorting: true,
			enableColumnFilterModes: true,
		}),
		columnHelper.accessor("confirmed_cer_code", {
			header: "CER",
			size: 100,
			enableSorting: true,
			enableColumnFilterModes: true,
		}),
		columnHelper.accessor("disposal_plant", {
			header: "Impianto smaltimento",
			size: 100,
			enableSorting: true,
			enableColumnFilterModes: true,
		}),
		columnHelper.accessor("wastes", {
			header: "Rifiuti",
			size: 100,
			enableSorting: true,
			enableColumnFilterModes: true,
			Cell: ({ cell }) => {
				const wastes = cell.getValue();

				return (
					<Box>
						{wastes.map((waste, index) => (
							<Typography key={index} variant="body2" sx={{ fontSize: "0.875rem", color: "#555" }}>
								{waste.material}: <Chip size="small" label={waste.quantity} sx={{ fontSize: "0.75rem" }} />
							</Typography>
						))}
					</Box>
				);
			},
		}),
	];

	const handleDocumentTypeChange = (event) => {
		setDocumentType(event.target.value);
		setDocuments([]); // Svuota l'array dei documenti quando cambia il tipo
		setHasSearched(false); // Resetta lo stato di ricerca
	};

	const handleAreaChange = (event, newValue) => {
		setSelectedArea(newValue);
	};

	// Add document type options
	const documentTypeOptions = [
		{ id: "first-copy", label: "Prima Copia" },
		{ id: "fourth-copy", label: "Quarta Copia" },
	];

	return (
		<DashboardLayout>
			<DashboardNavbar />
			<Box sx={{ padding: "20px" }}>
				<Paper
					elevation={0}
					sx={{
						padding: "30px",
						borderRadius: "12px",
						backgroundColor: "transparent!important",
						border: "none",
					}}
				>
					<Typography
						variant="h4"
						sx={{
							marginBottom: "24px",
							fontWeight: 600,
							color: "#333",
							fontSize: "1.75rem",
						}}
					>
						Esportazioni rifiuti
					</Typography>

					<Divider sx={{ marginBottom: "24px" }} />

					{/* Filters Section */}
					<Card
						variant="outlined"
						sx={{
							marginBottom: "30px",
							borderRadius: "10px",
							border: "1px solid #eaeaea",
							backgroundColor: "#ffffff",
							boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2), 0 1px 3px rgba(0, 0, 0, 0.06)",
						}}
					>
						<CardContent>
							<Stack direction="row" alignItems="center" spacing={1} sx={{ marginBottom: "16px" }}>
								<FilterAltIcon color="black" fontSize="medium" />
								<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
									Filtri di ricerca
								</Typography>
							</Stack>

							<Grid container spacing={3}>
								<Grid item xs={12} md={3}>
									<Autocomplete
										id="document-type"
										options={documentTypeOptions}
										getOptionLabel={(option) => option.label}
										value={documentTypeOptions.find((option) => option.id === documentType) || null}
										onChange={(event, newValue) => {
											if (newValue) {
												handleDocumentTypeChange({
													target: {
														value: newValue.id,
													},
												});
											}
										}}
										size="small"
										renderInput={(params) => (
											<TextField
												{...params}
												label="Tipo Documento"
												required
												variant="outlined"
												InputProps={{
													...params.InputProps,
													startAdornment: (
														<>
															<DescriptionIcon
																fontSize="small"
																sx={{
																	mr: 1,
																	color: "#666",
																}}
															/>
															{params.InputProps.startAdornment}
														</>
													),
												}}
											/>
										)}
									/>
								</Grid>

								{/* ... (rest of the Grid items remain the same) */}
								{!areaFromStorage && (
									<Grid item xs={12} md={3}>
										<Autocomplete
											id="area-select"
											options={aree}
											getOptionLabel={(option) => option.name || ""}
											value={selectedArea}
											onChange={handleAreaChange}
											size="small"
											renderInput={(params) => (
												<TextField {...params} label="Seleziona Area" fullWidth variant="outlined" required />
											)}
										/>
									</Grid>
								)}

								{areaFromStorage && selectedArea && (
									<Grid item xs={12} md={3}>
										<TextField
											fullWidth
											label="Cantiere"
											value={selectedArea.name}
											variant="outlined"
											size="small"
											disabled
										/>
									</Grid>
								)}

								<Grid item xs={12} md={3}>
									<LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="it">
										<DatePicker
											label="Data Inizio"
											value={startDate}
											onChange={(newValue) => setStartDate(newValue)}
											format="DD/MM/YYYY"
											slotProps={{
												textField: {
													fullWidth: true,
													size: "small",
													variant: "outlined",
													InputProps: {
														startAdornment: (
															<CalendarMonthIcon
																fontSize="small"
																sx={{
																	mr: 1,
																	color: "#666",
																}}
															/>
														),
													},
												},
											}}
										/>
									</LocalizationProvider>
								</Grid>

								<Grid item xs={12} md={3}>
									<LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="it">
										<DatePicker
											label="Data Fine"
											value={endDate}
											onChange={(newValue) => setEndDate(newValue)}
											format="DD/MM/YYYY"
											slotProps={{
												textField: {
													fullWidth: true,
													size: "small",
													variant: "outlined",
													InputProps: {
														startAdornment: (
															<CalendarMonthIcon
																fontSize="small"
																sx={{
																	mr: 1,
																	color: "#666",
																}}
															/>
														),
													},
												},
											}}
										/>
									</LocalizationProvider>
								</Grid>
							</Grid>

							{/* ... (rest of the filter section remains the same) */}
							<Box
								sx={{
									display: "flex",
									justifyContent: "flex-end",
									marginTop: "20px",
								}}
							>
								<Button
									variant="contained"
									color="primary"
									onClick={fetchDocuments}
									disabled={!selectedArea || searchLoading}
									startIcon={searchLoading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
									sx={{
										textTransform: "none",
										borderRadius: "8px",
										boxShadow: "none",
										padding: "8px 20px",
										color: "white!important",
									}}
								>
									{searchLoading ? "Ricerca in corso..." : "Cerca documenti"}
								</Button>
							</Box>
						</CardContent>
					</Card>

					{/* Content Section */}
					{loading || searchLoading ? (
						<Box
							sx={{
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
								height: "300px",
							}}
						>
							<CircularProgress size={120} sx={{ color: "#2b84ea" }} />
						</Box>
					) : (
						<>
							{hasSearched && documents.length > 0 ? (
								<Box>
									<Typography
										variant="subtitle1"
										sx={{
											fontWeight: 600,
											marginBottom: "16px",
										}}
									>
										Risultati della ricerca
									</Typography>

									{documentType === "first-copy" ? (
										<GenericTable data={documents} columns={columns_first_copy} authModelName="GARBAGEEXPORT" />
									) : (
										<GenericTable data={documents} columns={columns_fourth_copy} authModelName="GARBAGEEXPORT" />
									)}
								</Box>
							) : hasSearched && documents.length === 0 ? (
								<Box
									sx={{
										display: "flex",
										flexDirection: "column",
										alignItems: "center",
										justifyContent: "center",
										padding: "40px",
										textAlign: "center",
									}}
								>
									<DescriptionIcon
										sx={{
											fontSize: 60,
											color: "#aaa",
											marginBottom: "16px",
										}}
									/>
									<Typography variant="h6" color="textSecondary">
										Nessun documento trovato
									</Typography>
									<Typography
										variant="body2"
										color="textSecondary"
										sx={{
											maxWidth: "400px",
											marginTop: "8px",
										}}
									>
										Prova a modificare i filtri di ricerca o seleziona un intervallo di date diverso.
									</Typography>
								</Box>
							) : null}
						</>
					)}
				</Paper>
			</Box>
		</DashboardLayout>
	);
};

export default GarbageExport;
